import { useState, useCallback, useEffect, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useWizardStateManager } from '../utils/wizardStateManager';

/**
 * Custom hook for handling wizard navigation with unsaved changes protection
 */
export const useWizardNavigation = (options = {}) => {
  const {
    sessionId = null,
    parrotId = null,
    currentStep = 1,
    onSaveChanges = null,
    onNavigationBlocked = null,
    autoSave = false
  } = options;

  const navigate = useNavigate();
  const location = useLocation();
  const stateManager = useWizardStateManager(sessionId, parrotId);
  
  const [showConfirmDialog, setShowConfirmDialog] = useState(false);
  const [navigationIntent, setNavigationIntent] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  
  const pendingNavigationRef = useRef(null);
  const currentStepRef = useRef(currentStep);

  // Update current step ref when it changes
  useEffect(() => {
    currentStepRef.current = currentStep;
  }, [currentStep]);

  /**
   * Get step name for display
   */
  const getStepName = useCallback((step) => {
    const stepNames = {
      1: 'Basic Configuration',
      2: 'Voice Settings', 
      3: 'AI Behavior'
    };
    return stepNames[step] || `Step ${step}`;
  }, []);

  /**
   * Get route for a step
   */
  const getStepRoute = useCallback((step, isEditing = false) => {
    const baseRoute = isEditing && parrotId 
      ? `/parrots/${parrotId}/edit`
      : '/parrots/new';
    
    return `${baseRoute}/step${step}`;
  }, [parrotId]);

  /**
   * Check if navigation should be blocked
   */
  const shouldBlockNavigation = useCallback((targetStep, targetRoute = null) => {
    // Don't block if no unsaved changes
    if (!stateManager.hasUnsavedChanges()) {
      return false;
    }

    // Don't block navigation to the same step
    if (targetStep === currentStepRef.current) {
      return false;
    }

    // Don't block if auto-save is enabled and we have a save function
    if (autoSave && onSaveChanges) {
      return false;
    }

    return true;
  }, [stateManager, autoSave, onSaveChanges]);

  /**
   * Handle navigation attempt
   */
  const handleNavigationAttempt = useCallback(async (targetStep, targetRoute = null, options = {}) => {
    const { force = false, saveFirst = false } = options;

    // If forcing navigation or no unsaved changes, proceed immediately
    if (force || !shouldBlockNavigation(targetStep, targetRoute)) {
      if (targetRoute) {
        navigate(targetRoute);
      } else {
        const route = getStepRoute(targetStep, !!parrotId);
        navigate(route);
      }
      return true;
    }

    // If auto-save is enabled, try to save first
    if (saveFirst && onSaveChanges) {
      try {
        setIsLoading(true);
        await onSaveChanges();
        
        // After successful save, proceed with navigation
        if (targetRoute) {
          navigate(targetRoute);
        } else {
          const route = getStepRoute(targetStep, !!parrotId);
          navigate(route);
        }
        return true;
      } catch (error) {
        console.error('Failed to save before navigation:', error);
        // Continue to show confirmation dialog
      } finally {
        setIsLoading(false);
      }
    }

    // Store navigation intent and show confirmation dialog
    const intent = {
      targetStep,
      targetRoute,
      targetStepName: getStepName(targetStep),
      timestamp: new Date().toISOString()
    };

    setNavigationIntent(intent);
    stateManager.setNavigationIntent(targetStep, targetRoute);
    setShowConfirmDialog(true);

    // Notify parent component
    if (onNavigationBlocked) {
      onNavigationBlocked(intent);
    }

    return false;
  }, [
    shouldBlockNavigation, 
    navigate, 
    getStepRoute, 
    parrotId, 
    onSaveChanges, 
    getStepName, 
    stateManager, 
    onNavigationBlocked
  ]);

  /**
   * Navigate to a specific step
   */
  const navigateToStep = useCallback((step, options = {}) => {
    return handleNavigationAttempt(step, null, options);
  }, [handleNavigationAttempt]);

  /**
   * Navigate to a specific route
   */
  const navigateToRoute = useCallback((route, options = {}) => {
    return handleNavigationAttempt(null, route, options);
  }, [handleNavigationAttempt]);

  /**
   * Handle back navigation
   */
  const goBack = useCallback((options = {}) => {
    const previousStep = Math.max(1, currentStepRef.current - 1);
    return navigateToStep(previousStep, options);
  }, [navigateToStep]);

  /**
   * Handle forward navigation
   */
  const goForward = useCallback((options = {}) => {
    const nextStep = Math.min(3, currentStepRef.current + 1);
    return navigateToStep(nextStep, options);
  }, [navigateToStep]);

  /**
   * Handle confirmation dialog - save and continue
   */
  const handleSaveAndContinue = useCallback(async () => {
    if (!navigationIntent || !onSaveChanges) return;

    try {
      setIsLoading(true);
      
      // Save current changes
      await onSaveChanges();
      
      // Mark current step as completed
      stateManager.markStepCompleted(currentStepRef.current);
      
      // Proceed with navigation
      if (navigationIntent.targetRoute) {
        navigate(navigationIntent.targetRoute);
      } else if (navigationIntent.targetStep) {
        const route = getStepRoute(navigationIntent.targetStep, !!parrotId);
        navigate(route);
      }
      
      // Clean up
      setShowConfirmDialog(false);
      setNavigationIntent(null);
      stateManager.clearNavigationIntent();
      
    } catch (error) {
      console.error('Failed to save and continue:', error);
      // Keep dialog open to allow user to try again or choose different option
    } finally {
      setIsLoading(false);
    }
  }, [navigationIntent, onSaveChanges, stateManager, navigate, getStepRoute, parrotId]);

  /**
   * Handle confirmation dialog - continue without saving
   */
  const handleContinueWithoutSaving = useCallback(() => {
    if (!navigationIntent) return;

    // Clear unsaved changes
    stateManager.clearState();
    
    // Proceed with navigation
    if (navigationIntent.targetRoute) {
      navigate(navigationIntent.targetRoute);
    } else if (navigationIntent.targetStep) {
      const route = getStepRoute(navigationIntent.targetStep, !!parrotId);
      navigate(route);
    }
    
    // Clean up
    setShowConfirmDialog(false);
    setNavigationIntent(null);
    stateManager.clearNavigationIntent();
  }, [navigationIntent, stateManager, navigate, getStepRoute, parrotId]);

  /**
   * Handle confirmation dialog - cancel
   */
  const handleCancelNavigation = useCallback(() => {
    setShowConfirmDialog(false);
    setNavigationIntent(null);
    stateManager.clearNavigationIntent();
  }, [stateManager]);

  /**
   * Get changes summary for confirmation dialog
   */
  const getChangesSummary = useCallback(() => {
    return stateManager.getChangesSummary();
  }, [stateManager]);

  /**
   * Save current step data
   */
  const saveCurrentStep = useCallback(async (data, options = {}) => {
    try {
      const { completed = false } = options;
      
      stateManager.saveState(currentStepRef.current, data, { completed });
      
      if (completed) {
        stateManager.markStepCompleted(currentStepRef.current, data);
      }
      
      return true;
    } catch (error) {
      console.error('Failed to save current step:', error);
      return false;
    }
  }, [stateManager]);

  /**
   * Load step data
   */
  const loadStepData = useCallback((step) => {
    return stateManager.getStepData(step);
  }, [stateManager]);

  /**
   * Check if step is completed
   */
  const isStepCompleted = useCallback((step) => {
    return stateManager.isStepCompleted(step);
  }, [stateManager]);

  /**
   * Clear all wizard state
   */
  const clearWizardState = useCallback(() => {
    return stateManager.clearState();
  }, [stateManager]);

  /**
   * Handle browser back/forward navigation
   */
  useEffect(() => {
    const handlePopState = (event) => {
      // If there are unsaved changes, prevent default and show confirmation
      if (stateManager.hasUnsavedChanges()) {
        event.preventDefault();
        
        // Store the intended navigation
        const targetRoute = location.pathname;
        setNavigationIntent({
          targetRoute,
          targetStepName: 'Previous Page',
          timestamp: new Date().toISOString()
        });
        
        setShowConfirmDialog(true);
        
        // Push current state back to prevent navigation
        window.history.pushState(null, '', location.pathname);
      }
    };

    window.addEventListener('popstate', handlePopState);
    
    return () => {
      window.removeEventListener('popstate', handlePopState);
    };
  }, [stateManager, location.pathname]);

  /**
   * Handle page unload (browser close/refresh)
   */
  useEffect(() => {
    const handleBeforeUnload = (event) => {
      if (stateManager.hasUnsavedChanges()) {
        event.preventDefault();
        event.returnValue = 'You have unsaved changes. Are you sure you want to leave?';
        return event.returnValue;
      }
    };

    window.addEventListener('beforeunload', handleBeforeUnload);
    
    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
    };
  }, [stateManager]);

  return {
    // Navigation functions
    navigateToStep,
    navigateToRoute,
    goBack,
    goForward,
    
    // State management
    saveCurrentStep,
    loadStepData,
    isStepCompleted,
    clearWizardState,
    
    // Confirmation dialog
    showConfirmDialog,
    navigationIntent,
    handleSaveAndContinue,
    handleContinueWithoutSaving,
    handleCancelNavigation,
    getChangesSummary,
    
    // Status
    hasUnsavedChanges: stateManager.hasUnsavedChanges(),
    isLoading,
    
    // Utilities
    getStepName,
    getStepRoute,
    stateManager
  };
};
